import i18n from '../i18n'
import { errorCode } from '../services/api'
import { faDigits } from './numerals'

/**
 * Tooti+ (design page 19, `paywall`; P-paywall of the go-live plan). Since
 * tooti#121 (2026-09-18) the plans are the client's own: two monthly plans,
 * standard and premium, premium adding the Arcade library — and a seven-day
 * free trial, once per phone number. The prices are the server's
 * (`subscriptions_plans`); the ones in `paywall.json` only stand in until
 * that answer arrives, so a price change on the server needs no release here.
 *
 * A plain lib file, not a component, so it reads `i18n.t()` directly — the
 * `paywall` namespace. `PLANS` is a getter object (not a static literal) so
 * its text reads the current language rather than the one active at import
 * time.
 */

const t = (key, options) => i18n.t(key, { ns: 'paywall', ...options })

/** The plans on sale, in the order the page lists them. */
export const PLAN_KEYS = ['standard', 'premium']

function planText(key) {
  return {
    key,
    name: t(`plans.${key}.name`),
    note: t(`plans.${key}.note`),
    price: t(`plans.${key}.price`),
    perks: t(`plans.${key}.perks`, { returnObjects: true }),
  }
}

export const PLANS = {
  get standard() {
    return planText('standard')
  },
  get premium() {
    return planText('premium')
  },
}

/**
 * Rial minor units (a decimal string on the wire, ADR-0007) as the Toman
 * price a learner reads: «۲٬۹۰۰٬۰۰۰ تومان» / "2,900,000 Toman". BigInt, so
 * no float ever touches money; the one divide is Rial → Toman.
 */
export function tomanPrice(priceMinor) {
  let toman
  try {
    toman = (BigInt(priceMinor) / 10n).toString()
  } catch {
    return null
  }
  const fa = i18n.language === 'fa'
  const grouped = toman.replace(/\B(?=(\d{3})+(?!\d))/g, fa ? '٬' : ',')
  return t('priceToman', { price: faDigits(grouped) })
}

/**
 * The plans to draw: the static text, with the server's price over the
 * placeholder once `subscriptions_plans` has answered. A key the server
 * does not sell is dropped; one this build does not know is ignored.
 */
export function plansWithPrices(serverPlans) {
  return PLAN_KEYS.map((key) => {
    const plan = PLANS[key]
    const server = serverPlans?.find((p) => p.key === key)
    const price = server ? tomanPrice(server.priceMinor) : null
    return price ? { ...plan, price } : plan
  })
}

/** The name of whatever the learner has, for the profile row and the account card. */
export function planLabel(status) {
  if (status?.active) return PLANS[status.plan]?.name ?? (status.plan === 'monthly' || status.plan === 'yearly' ? t('legacyPlan') : t('freeLabel'))
  if (status?.trial?.state === 'active') return t('trialLabel', { days: faDigits(status.trial.daysLeft) })
  return t('freeLabel')
}

/** The sentence on the trial card, from `subscriptions_status.trial` and the plans' `trialDays`. */
export function trialLine(status, trialDays = 7) {
  const trial = status?.trial
  if (!trial || trial.state === 'available') return t('trial.available', { days: faDigits(trialDays) })
  if (trial.state === 'active') return t('trial.active', { days: faDigits(trial.daysLeft) })
  return status.locked ? t('trial.locked') : t('trial.used')
}

/**
 * The sentence for why a purchase or a trial start did not go through. A
 * request that never reached the server is offline, not a refusal — same
 * rule as the shop's.
 */
export function refusalFor(error) {
  if (!error?.response) {
    return { good: false, text: t('refusal.offline') }
  }
  const code = errorCode(error)
  if (code === 'subscriptions_not_enabled') return { good: false, text: t('refusal.notEnabled') }
  if (code === 'trial_already_used') return { good: false, text: t('refusal.trialUsed') }
  if (code === 'already_subscribed') return { good: false, text: t('refusal.alreadySubscribed') }
  return { good: false, text: t('refusal.default') }
}

/** Which plan the page opens on: the one a link asked for (`?plan=`), else premium — the one the client leads with. */
export function initialPlan(search) {
  const asked = new URLSearchParams(search ?? '').get('plan')
  return PLAN_KEYS.includes(asked) ? asked : 'premium'
}

/** An instant as a date the learner reads — Jalali in Persian, presentation only (ADR-0008). */
export function untilDate(isoInstant) {
  const date = new Date(isoInstant)
  if (Number.isNaN(date.getTime())) return ''
  const locale = i18n.language === 'fa' ? 'fa-IR-u-ca-persian' : 'en-GB'
  return new Intl.DateTimeFormat(locale, { dateStyle: 'long' }).format(date)
}
